<template>
  <span>
    <b-button
      v-b-modal="modalId"
      v-b-tooltip.hover
      title="Edit Profile Highlight"
      class="mt-2 mb-2 btn-orange"
      size="sm"
    ><i class="fas fa-pencil-alt" /></b-button>

    <b-modal
      :id="modalId"
      :title="modalTitle"
      :size="'xl'"
      @shown="onModalShown"
      @hidden="onModalHidden"
    >

      <template #default="{ hide }">
        <!-- Make sure vue-editor-clean-text component is registered globally or imported -->
        <!-- Assuming vue-editor-clean-text emits 'update:content' for sync -->
        <vue-editor-clean-text
          ref="profileHighlightEditor"
          :content.sync="summaryText"
        />

        <div
          v-if="errors.length > 0"
          class="mt-2"
        >
          <p
            v-for="(error, i) in errors"
            :key="`err-${i}`"
            class="text-danger small"
          >{{ error }}</p>
        </div>
        <div
          v-if="generationError"
          class="mt-2 text-danger small"
        >
          {{ generationError }}
        </div>
      </template>

      <template #modal-footer="{ ok, cancel }">
        <!-- Generate Bio Button -->
        <b-button
          size="sm"
          variant="info"
          :disabled="isGeneratingBio || hideRequest"
          @click="generateBio"
        >
          <b-spinner
            v-if="isGeneratingBio"
            small
          />
          <i
            v-else
            class="fas fa-magic mr-1"
          /> <!-- Added magic icon -->
          Generate Bio
        </b-button>

        <!-- Existing Save Button -->
        <b-button
          size="sm"
          variant="success"
          :disabled="isGeneratingBio || hideRequest"
          @click="updateSummary"
        >
          <b-spinner
            v-if="hideRequest === true"
            small
          />
          Save
        </b-button>
        <!-- Existing Cancel Button -->
        <b-button
          size="sm"
          variant="danger"
          :disabled="isGeneratingBio || hideRequest"
          @click="cancel()"
        >Cancel</b-button>
      </template>

    </b-modal>
  </span>
</template>

<script>
import axios from 'axios'; // Make sure axios is imported or globally available
// Import vue-editor-clean-text if it's not globally registered
// import VueEditorCleanText from './path/to/VueEditorCleanText.vue';

export default {
    name: 'ResultSummaryUpdateButton',
    // components: { VueEditorCleanText }, // Register component if imported locally
    props: {
        expertId: {type: Number, required: true},
        expert: {type: Object, required: true},
        summary: { type: String, default: '' },
        action: {type: String, required: true}, // 'action' prop seems unused, check if needed
        assignmentId: {type: Number, required: true},
        projectTitle: {type: String, default: ''},
        bioDescriptionSource: {type: String, default: ''},
        projectDescription: {type: String, default: ''},
    },
    data () {
        return {
            summaryText: this.summary,
            hideRequest: false, // For save operation
            isGeneratingBio: false, // For bio generation operation
            modalTitle: 'Update Profile Highlight',
            errors: [], // Errors for save operation
            generationError: null, // Error for bio generation
        }
    },
    computed: {
        modalId: function () {
            return `hide-modal-${this.assignmentId}`
        },
    },
    watch: {
        summary: {
            immediate: true,
            handler(newVal) {
                this.summaryText = newVal || ''
            }
        }
    },
    beforeDestroy() {
        // Cleanup - hide modal if component is destroyed
        if (this.modalId && this.$bvModal) {
            this.$bvModal.hide(this.modalId);
        }
    },
    methods: {
        onModalShown(){
            // Reset state specifically for showing the modal
            this.summaryText = this.summary || ''; // Ensure latest summary is loaded
            this.errors = [];
            this.generationError = null;
            this.hideRequest = false;
            this.isGeneratingBio = false;
            // You might need to manually focus or refresh the editor if needed
            // if (this.$refs.profileHighlightEditor) {
            //     // Assuming the editor component has a method to refresh or focus
            // }
        },
        onModalHidden() {
            // Reset state when modal is closed/hidden
            this.errors = [];
            this.generationError = null;
            this.hideRequest = false;
            this.isGeneratingBio = false;
        },
        updateSummary () {
            this.hideRequest = true;
            this.errors = [];
            this.generationError = null; // Clear generation errors on save attempt

            axios.patch(`/api/v2/assignments/${this.assignmentId}`,
                {
                    data: {
                        type: 'assignments',
                        id: this.assignmentId.toString(),
                        attributes: {
                            expert_summary: this.summaryText,
                        },
                    },
                },
                {
                    headers: {
                        'Content-Type': 'application/vnd.api+json',
                        'Accept': 'application/vnd.api+json',
                    },
                })
                .then(res => {
                    this.$emit('update-summary', this.summaryText);
                    this.$bvModal.hide(this.modalId);
                    this.$bvModal.msgBoxOk(`Profile highlight updated successfully.`);
                })
                .catch(err => {
                    console.error("Error updating summary:", err);
                    this.errors = ['Failed to update profile highlight. Please try again.'];
                    if (err.response?.data?.errors) {
                        // Assuming errors are in JSON:API format or similar
                        this.errors = err.response.data.errors.map(e => e.detail || e.title || 'An unknown error occurred.');
                    } else if (err.message) {
                        this.errors = [err.message];
                    }
                })
                .finally(() => {
                    this.hideRequest = false;
                });
        },
        generateBio() {
            this.isGeneratingBio = true;
            this.generationError = null; // Clear previous generation errors
            this.errors = []; // Clear save errors

            // Determine the description to send.
            // Use bioDescriptionSource prop if provided and not empty, otherwise use current editor content.
            const linkedinBio = this.bioDescriptionSource?.trim() ? this.bioDescriptionSource : this.summaryText;
            const descriptionToSendTrimmed = this.projectDescription?.trim();
            const expertName = this.expert.full_name;
            const sortedExperiences = this.convertSortedJobExperiences(this.expert.sorted_job_experiences)

            /*if (!descriptionToSend?.trim()) {
                this.generationError = 'Cannot generate bio from empty description.';
                this.isGeneratingBio = false;
                return;
            }*/

            axios.post('/api/generate-expert-bio', {
                assignment_id: parseInt(this.assignmentId),
                expert_id: parseInt(this.expertId),
                expert_name: expertName,
                linkedin_bio: linkedinBio,
                project_title: this.projectTitle,
                description: descriptionToSendTrimmed,
                experiences: sortedExperiences
            })
                .then(response => {
                    if (response.data && response.data.generated_bio) {
                        // Update the editor content directly
                        const generatedBio = response.data.generated_bio
                        this.summaryText = `<p>${generatedBio.replace(/\\/g, "")}</p>`
                        // Optionally notify user
                        // this.$bvToast.toast('Bio generated and updated in the editor.', { title: 'Success', variant: 'success', solid: true });

                        // Trigger update in the editor component if needed
                        // If vue-editor-clean-text needs an explicit event:
                        this.$nextTick(() => {
                            if (this.$refs.profileHighlightEditor && typeof this.$refs.profileHighlightEditor.setContent === 'function') {
                                this.$refs.profileHighlightEditor.setContent(this.summaryText);
                            }
                        });

                    } else {
                        this.generationError = 'Received an unexpected response from the server.';
                    }
                })
                .catch(err => {
                    console.error("Error generating bio:", err);
                    if (err.response?.data?.detail) {
                        this.generationError = `Error: ${err.response.data.detail}`;
                    } else if (err.response?.data?.error) {
                        this.generationError = `Error: ${err.response.data.error}`;
                    } else if (err.response?.data?.message) {
                        this.generationError = `Error: ${err.response.data.message}`;
                    } else {
                        this.generationError = 'An error occurred while generating the bio. Please try again.';
                    }
                })
                .finally(() => {
                    this.isGeneratingBio = false;
                });
        },
        convertSortedJobExperiences(sortedExperiences) {
            // Map each experience to the required string format
            return sortedExperiences.map(exp => {
                // Get company name from either `company_name` object or string
                const company = exp.company_name?.company_name || exp.company_name || '';
                const position = exp.position || '';
                return `${position} - ${company}`;
            });
        }

    }
}
</script>

<style scoped>
/* Add any specific styles if needed */
.btn-orange {
    /* Define your btn-orange style or ensure it's globally available */
    background-color: orange;
    border-color: orange;
    color: white;
}

.mr-1 {
    margin-right: 0.25rem; /* Utility class for margin */
}
</style>
